class FilterArticle extends HTMLElement {
    constructor() {
        super();

        // Initialize elements
        this.articleWrapper = document.querySelector('#main-article-list');
        this.filterItems = this.querySelectorAll('.filter-item');
        this.loadMoreBtn = document.querySelector('#load-more-button');

        // Store initial articles
        this.initialArticles = Array.from(this.articleWrapper.querySelectorAll('.news-card-block'));

        // Initialize state with multiple filter support
        this.state = {
            currentPage: 1,
            isLoading: false,
            activeFilters: new Set(), // Store multiple active filters
            baseUrl: null,
            cachedArticles: new Map(), // Cache articles for each filter
        };

        // Store initial articles in cache
        this.state.cachedArticles.set('initial', this.initialArticles);

        // Store base URL from the first filter item
        const firstFilter = this.filterItems[0];
        if (firstFilter) {
            this.state.baseUrl = firstFilter.dataset.baseurl;
        }

        // Bind events
        this.filterItems.forEach((item) => {
            item.addEventListener('click', this.handleFilterClick.bind(this));
        });

        if (this.loadMoreBtn) {
            this.loadMoreBtn.addEventListener('click', this.handleLoadMore.bind(this));
            this.loadMoreBtn.querySelector('.load-more-text').textContent = 'Load More';
        }

        // Log initial state
        console.log('Initial state:', {
            articleCount: this.initialArticles.length,
            baseUrl: this.state.baseUrl
        });
    }

    async handleFilterClick(event) {
        const filterItem = event.currentTarget; // Changed from event.target to event.currentTarget
        const checkbox = filterItem.querySelector('input');
        const filterId = filterItem.dataset.target.replace('article_', '');
        
        // Toggle checkbox state
        if (checkbox) {
            checkbox.checked = !checkbox.checked;
        }
        
        // Toggle active class
        filterItem.classList.toggle('active');

        console.log('Filter clicked:', {
            filterId,
            isChecked: checkbox?.checked,
            filterUrl: filterItem.dataset.url
        });

        // Update active filters
        if (checkbox && checkbox.checked) {
            this.state.activeFilters.add(filterId);
            await this.fetchFilterContent(filterItem.dataset.url, filterId);
        } else {
            this.state.activeFilters.delete(filterId);
            this.state.cachedArticles.delete(filterId);
        }

        // Update displayed articles
        this.updateDisplayedArticles();

        // Reset pagination
        this.resetPagination();
    }

    resetPagination() {
        this.state.currentPage = 1;
        if (this.loadMoreBtn) {
            this.loadMoreBtn.style.display = 'inline-flex';
        }
    }

    async handleLoadMore() {
        if (this.state.isLoading) return;

        try {
            this.state.isLoading = true;
            this.loadMoreBtn.querySelector('.load-more-text').textContent = 'Loading...';

            const nextPage = this.state.currentPage + 1;
            const loadMorePromises = [];

            // Make separate requests for each active filter
            for (const filterId of this.state.activeFilters) {
                const filterItem = Array.from(this.filterItems).find(item => 
                    item.dataset.target.replace('article_', '') === filterId
                );
                if (filterItem) {
                    const url = `${filterItem.dataset.url}?page=${nextPage}`;
                    loadMorePromises.push(this.fetchPageContent(url, filterId));
                }
            }

            // If no filters active, load unfiltered content
            if (this.state.activeFilters.size === 0) {
                const url = `${this.state.baseUrl}?page=${nextPage}`;
                loadMorePromises.push(this.fetchPageContent(url, 'initial'));
            }

            await Promise.all(loadMorePromises);
            
            // Update the display with new articles
            const hasNewContent = this.updateDisplayedArticles(true);
            
            if (hasNewContent) {
                this.state.currentPage = nextPage;
            } else {
                this.loadMoreBtn.style.display = 'none';
            }

        } catch (error) {
            console.error('Error loading more articles:', error);
        } finally {
            this.state.isLoading = false;
            this.loadMoreBtn.querySelector('.load-more-text').textContent = 'Load More';
        }
    }

    async fetchPageContent(url, filterId) {
        try {
            console.log('Fetching page content:', { url, filterId });
            const response = await fetch(url);
            const text = await response.text();
            const html = new DOMParser().parseFromString(text, 'text/html');
            const newArticles = Array.from(html.querySelectorAll('.news-card-block'));
            
            console.log('Fetched articles:', {
                filterId,
                count: newArticles.length
            });

            // Add new articles to cache
            const cachedArticles = this.state.cachedArticles.get(filterId) || [];
            this.state.cachedArticles.set(filterId, [...cachedArticles, ...newArticles]);
            
            return newArticles;
        } catch (error) {
            console.error(`Error fetching content for ${filterId}:`, error);
            return [];
        }
    }

    async fetchFilterContent(url, filterId) {
        try {
            console.log('Fetching filter content:', { url, filterId });
            const response = await fetch(url);
            const text = await response.text();
            const html = new DOMParser().parseFromString(text, 'text/html');
            const articles = Array.from(html.querySelectorAll('.news-card-block'));
            
            console.log('Fetched filter articles:', {
                filterId,
                count: articles.length
            });

            // Cache the articles for this filter
            this.state.cachedArticles.set(filterId, articles);
            
        } catch (error) {
            console.error('Error fetching filter content:', error);
            this.state.cachedArticles.set(filterId, []);
        }
    }

    updateDisplayedArticles(isLoadMore = false) {
        console.log('Updating displayed articles:', {
            isLoadMore,
            activeFilters: Array.from(this.state.activeFilters),
            cachedArticlesKeys: Array.from(this.state.cachedArticles.keys())
        });

        const existingUrls = new Set();
        
        // If not loading more, clear the current articles
        if (!isLoadMore) {
            this.articleWrapper.innerHTML = '';
        } else {
            // Get existing article URLs to avoid duplicates
            Array.from(this.articleWrapper.querySelectorAll('.read-more')).forEach(link => {
                existingUrls.add(link.getAttribute('href'));
            });
        }

        let hasNewContent = false;

        // If no filters are active, show initial/unfiltered articles
        if (this.state.activeFilters.size === 0) {
            const unfiltered = this.state.cachedArticles.get('initial') || [];
            unfiltered.forEach(article => {
                const url = article.querySelector('.read-more')?.getAttribute('href');
                if (!existingUrls.has(url)) {
                    this.articleWrapper.appendChild(article.cloneNode(true));
                    existingUrls.add(url);
                    hasNewContent = true;
                }
            });
            return hasNewContent;
        }

        // Combine articles from all active filters
        for (const filterId of this.state.activeFilters) {
            const articles = this.state.cachedArticles.get(filterId) || [];
            articles.forEach(article => {
                const url = article.querySelector('.read-more')?.getAttribute('href');
                if (!existingUrls.has(url)) {
                    this.articleWrapper.appendChild(article.cloneNode(true));
                    existingUrls.add(url);
                    hasNewContent = true;
                }
            });
        }

        console.log('Display update complete:', {
            totalDisplayed: this.articleWrapper.querySelectorAll('.news-card-block').length,
            hasNewContent
        });

        return hasNewContent;
    }
}

customElements.define('filter-article', FilterArticle);